import {useCallback} from 'react';
import {useDispatch} from 'react-redux';
import {useParams} from 'react-router-dom';

import {useCachedTable} from 'src/hooks/common-data-service/cached-table';
import {getGroupMembersCounts} from 'src/openapi/admin/apis/GetGroupsMembersCountsApi';
import {useAppDispatch} from 'src/store/hooks';

import {
    groupsTabMembersActions,
    groupsTabMembersDataSelector,
    groupsTabMembersResultSelector,
} from '../slices/groups-tab-members.slice';
import {groupsTabStateSelector} from '../slices/groups-tab-state.slice';

import type {GroupsTabCacheKeys} from '../typedef';

type Props = {
    groupIds: number[],
    totalCount: number,
};

export const useGetGroupsTabMembers = ({groupIds, totalCount}: Props) => {
    const apiDispatch = useAppDispatch();
    const dispatch = useDispatch();
    const queryParams = useParams<{accountId: string}>();
    const accountId = queryParams.accountId ? +queryParams.accountId : -1;

    const getAction = useCallback(() => apiDispatch(getGroupMembersCounts({
        accountId,
        requestBody: groupIds,
    })), [accountId, groupIds, apiDispatch]);

    const setLoadedData = useCallback((
        response: Record<string, string>,
        dataCacheKeys: GroupsTabCacheKeys,
    ) => {
        dispatch(groupsTabMembersActions.onSuccess(({
            cacheKeys: dataCacheKeys,
            totalCount,
            data: response,
        })));
    }, [totalCount, dispatch]);

    return useCachedTable({
        dataSelector: groupsTabMembersDataSelector,
        stateSelector: groupsTabStateSelector,
        resultSelector: groupsTabMembersResultSelector,
        actions: groupsTabMembersActions,
        getAction,
        onSuccessLoad: setLoadedData,
        shouldClearOnUnmount: false,
    });
};
